import { Component, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import {
  Currency,
  DateRange,
  extractDateRange,
  extractDefaultStoreIds,
  extractNumberArr,
  getCurrency,
  getCurrencySymbol,
  HxMoneyFormatPipe,
  HxReportService,
  HxStoreService,
  HxUiLabelPipe,
  isoDate,
  MoneyFlowInfoModel,
  MoneyFlowModel,
  moneyFormat,
  OrderAction,
  StoreBasicModel,
  toFixed,
  toRouterQueryParams,
  TotalSpendingModel,
  ViewType
} from 'hx-services';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import type { Chart, XYChart } from '@amcharts/amcharts4/charts';
import { ToastrService } from 'ngx-toastr';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';

@Component({
    selector: 'app-money-flow-report.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './money-flow-report.component.html',
    styleUrls: ['./money-flow-report.component.css'],
    imports: [HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, NgSelectComponent, NgLabelTemplateDirective, NgOptionTemplateDirective, HxLoaderComponent, NgFor, NgClass, NgIf, RouterLink, DatePipe, TranslocoPipe, HxUiLabelPipe, HxMoneyFormatPipe]
})
export class MoneyFlowReportComponent implements OnInit, OnDestroy {
  @ViewChild('templateRef') public templateRef: TemplateRef<any>;
  list: MoneyFlowInfoModel[] = [];
  listByStore: MoneyFlowModel[] = [];
  params: { date?: DateRange, storeIds?: number[] } = {};
  viewTypeOptions: ViewType[] = [ViewType.SPLIT, ViewType.MERGED];
  viewType?: ViewType;
  isLoading = {
    list: false,
  };
  ViewType = ViewType;
  stores: StoreBasicModel[] = [];
  spendingMap = new Map<number, TotalSpendingModel[]>();
  totalSpendingMap = new Map<number, {totalRevenues: number, totalSpends: number, totalSales: number}>();
  spendings: TotalSpendingModel[] = [];
  totalSpendingInfo: {totalRevenues: number, totalSpends: number, totalSales: number};
  refundAction = OrderAction.REFUNDED;
  selected?: TotalSpendingModel;
  private sub: Subscription;
  private charts: Chart[] = [];

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private errorHandlerService: ErrorHandlerService,
    private storeService: HxStoreService,
    private toastr: ToastrService,
    private zone: NgZone,
    private tr:TranslocoService,
    private modal: NgbModal,
  ) {
  }

  browserOnly(fn: () => void) {
    this.zone.runOutsideAngular(() => fn());
  }

  ngOnInit() {
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      this.params.storeIds = extractNumberArr('storeIds', paramMap);
      if (this.params.storeIds.length === 0) {
        this.params.storeIds = extractDefaultStoreIds();
      }
      this.viewType = paramMap.get('viewType') ? ViewType[paramMap.get('viewType')] : ViewType.SPLIT;
      if (this.params.storeIds.length > 0) {
        this.storeService.getStoreByIds(this.params.storeIds).then(stores => {
          this.stores = stores;
          this.loadMoneyFlowInfos();
          this.loadSpendings();
        });
      } else {
        this.stores = [];
        this.list = [];
        this.listByStore = [];
        this.disposeCharts();
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    this.disposeCharts();
  }

  getTypeFilter(): void {
    this.navigate();
  }

  showRefunds(item: TotalSpendingModel) {
    this.selected = {...item};
    this.modal.open(this.templateRef, { size: 'lg', centered: true });
  }

  private disposeCharts() {
    this.browserOnly(() => {
      this.charts.forEach(chart => chart.dispose());
      this.charts = [];
    });
  }

  private async loadSpendings() {
    this.isLoading.list = true;
    this.spendingMap.clear();
    this.spendings = [];
    try {
      if (this.viewType === ViewType.MERGED) {
        this.spendings = await this.reportService.getTotalSpendingsByCategory({ fromDate: this.params.date.from, storeIds: this.params.storeIds, toDate: this.params.date.to });
      } else {
        this.spendings = await this.reportService.getTotalSpendingsByCategoryAndStores({ fromDate: this.params.date.from, storeIds: this.params.storeIds, toDate: this.params.date.to });
        this.spendings.forEach(sp => {
          const arr = this.spendingMap.get(sp.storeId) ?? [];
          arr.push(sp);
          this.spendingMap.set(sp.storeId, arr);
        });
      }
    } finally {
      this.isLoading.list = false;
    }
  }

  private loadMoneyFlowInfos() {
    this.isLoading.list = true;
    this.list = [];
    this.disposeCharts();
    this.listByStore = [];
    if (this.viewType === ViewType.MERGED) {
      this.reportService.getMoneyFlowInfos(this.params).subscribe(result => {
        this.list = result || [];
        this.renderMergedChart();
        this.isLoading.list = false;
      }, error => {
        this.errorHandlerService.handler(error);
        this.isLoading.list = false;
      });
    } else {
      this.reportService.getMoneyFlows(this.params).subscribe(result => {
        this.listByStore = result || [];
        this.renderSplittedCharts();
        this.isLoading.list = false;
      }, error => {
        this.errorHandlerService.handler(error);
        this.isLoading.list = false;
      });
    }
  }

  private renderSplittedCharts() {
    this.totalSpendingMap.clear();
    this.listByStore.forEach(l => {
      const chartData = l.moneyFlows.sort((a, b) => {
        const c = new Date(a.date);
        const d = new Date(b.date);
        return c.getTime() - d.getTime();
      })
        .map(r => ({
          date: r.date,
          totalReceives: r.totalReceive,
          totalRefunds: Math.abs(r.totalRefund),
          totalInvoices: Math.abs(r.totalInvoice)
        }));
      this.createChart(chartData, l.store.currency, l.store.id);
    });
  }

  private renderMergedChart() {
    const chartData = this.list.sort((a, b) => {
      const c = new Date(a.date);
      const d = new Date(b.date);
      return c.getTime() - d.getTime();
    }).map(r => ({
      date: r.date,
      totalReceives: r.totalReceive,
      totalRefunds: Math.abs(r.totalRefund),
      totalInvoices: Math.abs(r.totalInvoice)
    }));
    if (this.stores.length > 0) {
      const onlyOneCurrency = new Set(this.stores.map(s => s.currency)).size === 1;
      if (onlyOneCurrency) {
        this.createChart(chartData, this.stores[0].currency);
      } else {
        this.toastr.error(this.tr.translate('money-flow-report.ts.chooseShop'));
      }
    }
  }

  private navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams({
        ...this.params,
        viewType: this.viewType,
      }),
      relativeTo: this.aRoute
    });
  }

  private createChart(chartData: any[], currency: Currency, storeId?: number) {
    let id = null;
    let currencyStr = '';
    if (currency) {
      currencyStr = getCurrency(currency)?.full ?? 'теңге';
    }
    if (chartData.length > 0) {
      if (this.viewType === ViewType.SPLIT) {
        id = 'chartdivsplit_' + storeId;
      } else {
        id = 'chartdivmerged';
      }

      const totalReceives = chartData.map(r => r.totalReceives).filter(t => t > 0).reduce((a, b) => a + b, 0);
      const totalRefunds = chartData.map(r => r.totalRefunds).filter(t => t > 0).reduce((a, b) => a + b, 0);
      const totalInvoices = chartData.map(r => r.totalInvoices).filter(t => t > 0).reduce((a, b) => a + b, 0);

      const totalRevenues = toFixed(totalReceives);
      const totalSpends = toFixed(totalRefunds + totalInvoices);
      const totalSales = toFixed(totalRevenues - totalSpends);

      if (this.viewType === ViewType.SPLIT) {
        this.totalSpendingMap.set(storeId, {totalRevenues: totalRevenues, totalSpends: totalSpends, totalSales: totalSales});
      } else {
        this.totalSpendingInfo = {totalRevenues: totalRevenues, totalSpends: totalSpends, totalSales: totalSales};
      }

      const data = chartData.map(res => ({
        date: res.date,
        receives: res.totalReceives,
        refunds: res.totalRefunds,
        invoices: res.totalInvoices,
        spendings: toFixed(res.totalRefunds + res.totalInvoices),
        total: toFixed(res.totalReceives - (res.totalRefunds + res.totalInvoices))
      }));

      // Chart code goes in here
      this.browserOnly(() => {
        Promise.all([
          import('@amcharts/amcharts4/core'),
          import('@amcharts/amcharts4/charts'),
          import('@amcharts/amcharts4/themes/animated').then(res => res.default)
        ]).then(([am4core, am4charts, am4themesAnimated]) => {
          am4core.useTheme(am4themesAnimated);

          const chart: XYChart = am4core.create(id, am4charts.XYChart);
          chart.data = data;

          const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
          dateAxis.dateFormats.setKey('day', 'dd.MM yyyy');
          dateAxis.tooltipDateFormat = '[bold]dd.MM.yyyy[/]';
          dateAxis.renderer.grid.template.location = 0;

          const valueAxis1 = chart.yAxes.push(new am4charts.ValueAxis());
          valueAxis1.title.text = currencyStr;

          const series1 = chart.series.push(new am4charts.ColumnSeries());
          series1.dataFields.valueY = 'total';
          series1.dataFields.dateX = 'date';
          series1.yAxis = valueAxis1;
          series1.name = this.tr.translate('money-flow-report.ts.profit', {totalSales: moneyFormat(totalSales)});
          series1.tooltip.label.interactionsEnabled = true;
          series1.tooltip.keepTargetHover = true;
          if (this.viewType === ViewType.SPLIT) {
            series1.adapter.add('tooltipHTML', () => `
              <span style="color: #ffffff">{name}<br>
                <a style="text-decoration: none; color: white" href="/reports/money?dateFrom={dateX}&dateTo={dateX}&storeId=${storeId}">
                  <b>{valueY}${getCurrencySymbol(currency)}</b>
                </a>
              </span>
            `);
          } else {
            series1.tooltipHTML = '<span style="color: #ffffff">{name}<br><b>{valueY}' + getCurrencySymbol(currency) + '</b></span>';
          }
          series1.fill = am4core.color('#7FE6AF');
          series1.strokeWidth = 0;
          series1.clustered = false;
          series1.columns.template.width = am4core.percent(30);

          const series2 = chart.series.push(new am4charts.LineSeries());
          series2.dataFields.valueY = 'receives';
          series2.dataFields.dateX = 'date';
          series2.stroke = am4core.color('#66B2FF');
          series2.fill = am4core.color('#66B2FF');
          series2.name = this.tr.translate('money-flow-report.ts.sale', {totalRevenues: moneyFormat(totalRevenues)});
          series2.strokeWidth = 2;
          series2.tensionX = 0.7;
          series2.yAxis = valueAxis1;
          series2.tooltip.label.interactionsEnabled = true;
          series2.tooltip.keepTargetHover = true;
          if (this.viewType === ViewType.SPLIT) {
            series2.adapter.add('tooltipHTML', () => `
              <span style="color: #ffffff">{name}<br>
                <a style="text-decoration: none; color: white" href="/reports/money?dateFrom={dateX}&dateTo={dateX}&storeId=${storeId}">
                    <b>{valueY}${getCurrencySymbol(currency)}</b>
                </a>
              <span
            `);
          } else {
            series2.tooltipHTML = `<span style="color: #ffffff">{name}<br><b>{valueY}${getCurrencySymbol(currency)}</b></span>`;
          }

          const series3 = chart.series.push(new am4charts.LineSeries());
          series3.dataFields.valueY = 'spendings';
          series3.dataFields.dateX = 'date';
          series3.dataFields.openValueX = 'refunds';
          series3.dataFields.openValueY = 'invoices';
          series3.name = this.tr.translate('money-flow-report.ts.expenses', {totalRevenues: moneyFormat(totalSpends)});
          series3.strokeWidth = 2;
          series3.tensionX = 0.7;
          series3.yAxis = valueAxis1;
          series3.stroke = am4core.color('#FF6E66');
          series3.fill = am4core.color('#FF6E66');
          series3.tooltip.label.interactionsEnabled = true;
          series3.tooltip.keepTargetHover = true;

          if (this.viewType === ViewType.SPLIT) {
            series3.adapter.add('tooltipHTML', () => `
              <span style="color: #ffffff; text-decoration: none">{name}: {spendings} <br>
                <a style="text-decoration: none; color: white" href="/reports/money?dateFrom={dateX}&dateTo={dateX}&storeId=${storeId}">
                    <b>${this.tr.translate('money-flow-report.ts.return')}:</b> {openValueX}${getCurrencySymbol(currency)}
                </a> <br>
                <a style="text-decoration: none; color: white" href="/invoices?date={dateX}&status=RECEIVED&storeId=${storeId}">
                    <b>${this.tr.translate('money-flow-report.ts.paid')}:</b> {openValueY}${getCurrencySymbol(currency)}
                </a>
              </span>
            `);
          } else {
            series3.tooltipHTML = `<span style="color: #ffffff">{name}<br><b>{valueY}${getCurrencySymbol(currency)}</b></span>`;
          }
          chart.cursor = new am4charts.XYCursor();

          chart.legend = new am4charts.Legend();
          chart.legend.position = 'top';
          this.charts.push(chart);
        }).catch((e) => {
          console.error('Error when creating chart', e);
        });
      });
    }
  }
}
